// Availability utilities for BuildMyShifts
// Manages recurring and one-off availability constraints

/**
 * Availability types
 */
export const AVAILABILITY_TYPE = {
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable',
  PREFERRED: 'preferred',
  LEAVE: 'leave',
};

/**
 * Recurrence patterns
 */
export const RECURRENCE = {
  NONE: 'none',
  WEEKLY: 'weekly',
  BIWEEKLY: 'biweekly',
  MONTHLY: 'monthly',
};

/**
 * Check if a staff member is available for a specific date and time range
 */
export function isStaffAvailable(staffId, date, startTime, endTime, availabilityRecords) {
  const dateObj = new Date(date);
  const dayOfWeek = dateObj.getDay(); // 0=Sun, 6=Sat

  // Filter to this staff member's records
  const records = availabilityRecords.filter(r => r.staffId === staffId);

  for (const record of records) {
    // Check one-off entries
    if (record.recurrence === RECURRENCE.NONE) {
      if (record.date === date) {
        if (record.type === AVAILABILITY_TYPE.UNAVAILABLE || record.type === AVAILABILITY_TYPE.LEAVE) {
          // Check if time overlaps
          if (!record.startTime || !record.endTime) return false; // All day
          if (timesOverlap(startTime, endTime, record.startTime, record.endTime)) {
            return false;
          }
        }
      }
      continue;
    }

    // Check recurring entries
    if (record.recurrence === RECURRENCE.WEEKLY) {
      if (record.dayOfWeek === dayOfWeek) {
        if (record.type === AVAILABILITY_TYPE.UNAVAILABLE) {
          if (!record.startTime || !record.endTime) return false;
          if (timesOverlap(startTime, endTime, record.startTime, record.endTime)) {
            return false;
          }
        }
      }
    }

    // Biweekly
    if (record.recurrence === RECURRENCE.BIWEEKLY) {
      if (record.dayOfWeek === dayOfWeek) {
        const recordStart = new Date(record.startDate);
        const weeksDiff = Math.floor((dateObj - recordStart) / (7 * 24 * 60 * 60 * 1000));
        if (weeksDiff % 2 === 0) {
          if (record.type === AVAILABILITY_TYPE.UNAVAILABLE) {
            if (!record.startTime || !record.endTime) return false;
            if (timesOverlap(startTime, endTime, record.startTime, record.endTime)) {
              return false;
            }
          }
        }
      }
    }
  }

  return true;
}

/**
 * Simple time overlap check (duplicated here to avoid circular dependency)
 */
function timesOverlap(start1, end1, start2, end2) {
  const toMin = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + (m || 0);
  };
  const s1 = toMin(start1);
  let e1 = toMin(end1);
  const s2 = toMin(start2);
  let e2 = toMin(end2);
  if (e1 <= s1) e1 += 24 * 60;
  if (e2 <= s2) e2 += 24 * 60;
  return s1 < e2 && s2 < e1;
}

/**
 * Get availability summary for a staff member for a given week
 */
export function getWeekAvailability(staffId, weekStart, availabilityRecords) {
  const days = [];
  const start = new Date(weekStart);

  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const dateStr = d.toISOString().split('T')[0];

    // Check availability for the full day (00:00 – 23:59)
    const available = isStaffAvailable(staffId, dateStr, '00:00', '23:59', availabilityRecords);

    days.push({
      date: dateStr,
      dayOfWeek: d.getDay(),
      available,
    });
  }

  return days;
}

/**
 * Create an availability record
 */
export function createAvailabilityRecord(staffId, type, options = {}) {
  return {
    staffId,
    type,
    recurrence: options.recurrence || RECURRENCE.NONE,
    date: options.date || null,
    dayOfWeek: options.dayOfWeek ?? null,
    startTime: options.startTime || null,
    endTime: options.endTime || null,
    startDate: options.startDate || null,
    endDate: options.endDate || null,
    reason: options.reason || '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

const availabilityUtils = {
  AVAILABILITY_TYPE,
  RECURRENCE,
  isStaffAvailable,
  getWeekAvailability,
  createAvailabilityRecord,
};
export default availabilityUtils;
